const dayMs = 86400000;
const zoned = new Intl.DateTimeFormat('en-CA', {timeZone:'Europe/Istanbul',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23'});
const weekday = new Intl.DateTimeFormat('en-US', {timeZone:'Europe/Istanbul',weekday:'short'});
const dateLabel = new Intl.DateTimeFormat('tr-TR', {timeZone:'Europe/Istanbul',day:'numeric',month:'long',weekday:'long'});
const validDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(value+'T12:00:00Z').toISOString().slice(0,10) === value;
const lines = value => String(value || '').split(/[\n,]/).map(x=>x.trim()).filter(Boolean);
const minuteOfDay = value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? Number(value.slice(0,2))*60+Number(value.slice(3)) : NaN;
const hhmm = value => `${String(Math.floor(value/60)).padStart(2,'0')}.${String(value%60).padStart(2,'0')}`;
const parts = epoch => Object.fromEntries(zoned.formatToParts(epoch).map(x=>[x.type,x.value]));
const dateKey = epoch => { const p=parts(epoch);return `${p.year}-${p.month}-${p.day}`; };
const dateEpoch = date => Date.parse(date+'T12:00:00+03:00');
export function buildSchedule(raw) {
  try {
    // 00:00 denotes the end of the order day, equivalent to 24:00 here.
    const cutoff=raw.cutoff==='00:00'?1440:NaN;
    if (!Number.isFinite(cutoff) || !Array.isArray(raw.weekdays) || raw.weekdays.length!==7 || !raw.weekdays.some(Boolean) || raw.weekdays.some(x=>typeof x!=='boolean') || !Array.isArray(raw.knownHandles) || raw.knownHandles.some(x=>typeof x!=='string'||!x)) return null;
    if (!validDate(raw.calendarFrom) || !validDate(raw.calendarUntil) || raw.calendarFrom>raw.calendarUntil) return null;
    const holidays=new Map(), exceptions=new Map(), closed=new Set(), preparation=new Map();
    for (const entry of lines(raw.holidays)) {
      const [date,time,...extra]=entry.split('|');
      if (!validDate(date) || extra.length || (time!==undefined && !Number.isFinite(minuteOfDay(time)))) return null;
      const close=time===undefined?0:minuteOfDay(time);
      holidays.set(date,Math.min(holidays.get(date)??1440,close));
    }
    for (const date of lines(raw.closedDates)) { if(!validDate(date))return null;closed.add(date); }
    for (const entry of lines(raw.exceptions)) {
      const [date,time,...extra]=entry.split('|');
      if(!validDate(date)||!Number.isFinite(minuteOfDay(time))||extra.length)return null;
      exceptions.set(date,Math.min(exceptions.get(date)??1440,minuteOfDay(time)));
    }
    for(const entry of lines(raw.preparationRules)) {
      const [handle,minutes,days,...extra]=entry.split('|');
      if(!/^[a-z0-9-]+$/.test(handle)||!/^\d+$/.test(minutes)||!/^\d+$/.test(days)||extra.length||Number(minutes)>1440||Number(days)>30)return null;
      preparation.set(handle,{minutes:Number(minutes),days:Number(days)});
    }
    for(const key of ['preparationMinutes','preparationDays','extraUnitMinutes'])if(!Number.isInteger(raw[key])||raw[key]<0||raw[key]>1440)return null;
    return {...raw,cutoff,holidays,exceptions,closed,preparation};
  } catch { return null; }
}
function opening(schedule,date) {
  if(date<schedule.calendarFrom||date>schedule.calendarUntil||schedule.closed.has(date))return null;
  const day=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(weekday.format(dateEpoch(date)));
  if(!schedule.weekdays[day]||schedule.holidays.has(date))return null;
  // Treat even half-day public holidays as non-dispatch dates unless the carrier calendar is explicitly re-confirmed.
  const cutoff=Math.min(schedule.cutoff,schedule.exceptions.get(date)??1440);
  return cutoff>0?cutoff:null;
}
export function evaluateDispatch(raw,epoch,items=[]) {
  if(raw.enabled!==true||raw.confirmed!==true)return {state:'disabled'};
  if(!Number.isFinite(epoch))return {state:'unverified'};
  const s=buildSchedule(raw);if(!s)return {state:'invalid'};
  if(!Array.isArray(items)||items.some(x=>!x||typeof x!=='object'))return {state:'unavailable'};
  items=items.flatMap(item=>Array.isArray(s.bundleComponents?.[item.h])?s.bundleComponents[item.h].map(h=>({...item,h})):item);
  const today=dateKey(epoch);
  if(today<s.calendarFrom||today>s.calendarUntil)return {state:'calendar_expired'};
  if(items.some(x=>x.available!==true||!Number.isInteger(x.q)||x.q<1))return {state:'unavailable'};
  if(items.some(x=>!s.knownHandles.includes(x.h)&&!s.preparation.has(x.h)))return {state:'unknown_product'};
  let prep=s.preparationMinutes,days=s.preparationDays,units=0,extra=0;
  for(const item of items){const rule=s.preparation.get(item.h);extra=Math.max(extra,rule?.minutes||0);days=Math.max(days,rule?.days||0);units+=item.q;}
  prep+=extra+Math.max(0,units-1)*s.extraUnitMinutes;
  // Orders placed up to midnight are eligible for the first following open
  // dispatch day. Only explicit preparation days/closures can add delay.
  let remainingPreparationDays=days;
  for(let i=1;i<=62;i++){
    const candidateDate=dateKey(dateEpoch(today)+i*dayMs);
    const close=opening(s,candidateDate);
    if(close===null||close<=prep)continue;
    if(remainingPreparationDays>0){remainingPreparationDays--;continue;}
    return {state:'next',date:candidateDate,label:dateLabel.format(dateEpoch(candidateDate)),cutoff:close===1440&&prep===0?'00.00':hhmm(close-prep),reason:days>0?'preparation':'eligible'};
  }
  return {state:'unverified'};
}
export function dispatchPresentation(result,epoch,strings,compact=false) {

  if(result.state==='next'){
    const today=dateKey(epoch),tomorrow=dateKey(dateEpoch(today)+dayMs);
    const nextDay=weekday.format(dateEpoch(result.date));
    const names=String(strings.weekdays).split('|');
    const day=names[['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(nextDay)];
    const distance=Math.round((dateEpoch(result.date)-dateEpoch(today))/dayMs);
    const title=result.date===tomorrow?strings.tomorrow:strings.nextDay.replace('{day}',distance<7?day:result.label);
    return {title,note:compact?strings.compactNext:strings.next.replace('{date}',result.label).replace('{time}',result.cutoff),countdown:null};
  }
  return {title:compact?strings.headerFallback:strings.fallbackTitle,note:compact?strings.compactFallback:strings.fallback,countdown:null};
}
export function serverEpoch(headers,elapsed) {
  const date=headers.get('Date'),age=headers.get('Age'),parsed=Date.parse(date||'');
  if(!Number.isFinite(parsed)||!Number.isFinite(elapsed)||elapsed<0||elapsed>5000||(age!==null&&(!/^\d+$/.test(age)||Number(age)>5)))return null;
  return parsed+Number(age||0)*1000+elapsed+1000;
}
function startDispatch() {
  const element=document.getElementById('TaplyDispatchConfig');
  if(!element)return;
  let config;
  try{config=JSON.parse(element.textContent);}catch{return;}
  if(config.enabled!==true||config.confirmed!==true||!buildSchedule(config))return;
  let clock=null,pending=null,controller=null,timer=null,lastTick=null,nextAttempt=0,generation=0;
  const strings=config.strings;
  const setText=(element,text)=>{if(element&&element.textContent!==text)element.textContent=text;};
  function render(){
    const tick=performance.now();
    if(lastTick!==null&&tick-lastTick>65000)clock=null;
    lastTick=tick;
    if(clock&&tick-clock.tick>180000)clock=null;
    const deviceNow=Date.now();
    // Use a fresh server-calibrated clock when available. A missing/stale AJAX Date header
    // must not permanently suppress eligible dispatch dates on mobile or desktop.
    // Date evaluation always uses Europe/Istanbul in the schedule helpers.
    const now=clock?clock.epoch+tick-clock.tick:deviceNow;
    document.querySelectorAll('[data-dispatch-widget]').forEach(widget=>{
      let items=[];try{items=JSON.parse(widget.dataset.items||'[]');}catch{items=[{available:false}];}
      const result=evaluateDispatch(config,now,items),compact=widget.dataset.compact==='true';
      const title=widget.querySelector('[data-dispatch-title]'),note=widget.querySelector('[data-dispatch-note]'),countdown=widget.querySelector('[data-dispatch-countdown]');
      widget.dataset.state=result.state;
      const presentation=dispatchPresentation(result,now,strings,compact);
      setText(title,presentation.title);setText(note,presentation.note);
      if(countdown){countdown.hidden=presentation.countdown===null;setText(countdown,presentation.countdown||'');}
    });
  }
  async function synchronize(){
    if(pending||performance.now()<nextAttempt||document.visibilityState==='hidden')return pending;
    pending=(async()=>{
      const requestGeneration=generation;
      controller=new AbortController();const timeout=setTimeout(()=>controller?.abort(),5000);
      try{
        const url=new URL(config.clockUrl,location.origin);
        if(url.origin!==location.origin)throw new Error('origin');
        url.searchParams.set('_taply_clock',crypto.randomUUID());
        const start=performance.now();
        const response=await fetch(url,{method:'GET',cache:'no-store',credentials:'same-origin',headers:{Accept:'application/json','Cache-Control':'no-cache'},signal:controller.signal});
        const epoch=serverEpoch(response.headers,performance.now()-start);
        if(!response.ok||!response.headers.get('Content-Type')?.includes('application/json')||epoch===null)throw new Error('clock');
        if(requestGeneration!==generation||document.visibilityState==='hidden'||navigator.onLine===false)return;
        clock={epoch,tick:performance.now()};
      }catch{clock=null;}finally{clearTimeout(timeout);controller=null;nextAttempt=performance.now()+60000;render();}
    })();
    try{await pending;}finally{pending=null;}
  }
  function resume(){clock=null;lastTick=null;nextAttempt=0;render();synchronize();if(!timer)timer=setInterval(()=>{render();if(!clock||performance.now()-clock.tick>=120000)synchronize();},30000);}
  function pause(){generation++;clearInterval(timer);timer=null;controller?.abort();clock=null;lastTick=null;render();}
  document.addEventListener('taply:selection-change',event=>{
    const widget=event.target.closest('[data-product-section]')?.querySelector('[data-dispatch-widget]');
    if(widget){widget.dataset.items=JSON.stringify(event.detail.items);render();}
  });
  document.addEventListener('taply:cart-updated',()=>render());
  document.addEventListener('visibilitychange',()=>document.visibilityState==='hidden'?pause():resume());
  window.addEventListener('pagehide',pause);window.addEventListener('pageshow',event=>{if(event.persisted)resume();});
  window.addEventListener('offline',pause);window.addEventListener('online',resume);
  document.addEventListener('shopify:section:load',render);
  resume();
}
if(typeof document!=='undefined')startDispatch();
